import React, {
  useCallback,
  useEffect,
  useMemo,
  useLayoutEffect,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';

import {
  ScrollView,
  StyleSheet,
  Text,
  View,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { GTFonts, useTheme } from '../../theme';
import {
  fs,
  s,
  v,
  formatWithCommas,
  formatNumberDecimal,
  getCurrencyDisplayFormat,
  getNumericDisplayDecimals,
  formatMoney,
} from '../../utils/libs';
import PaymentsBarChart from '../../components/charts/PaymentsBarChart';
import AppDataTable, {
  type AppDataTableColumn,
} from '../../components/table/AppDataTable';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import { useNavigation, useRoute } from '@react-navigation/native';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  setTransactions,
  setTransactionsError,
  setTransactionsLoading,
  setTransactionsPagination,
  setSalesTotals,
  setSalesTotalsError,
  setSalesTotalsLoading,
  setSalesOverTime,
} from '../../redux/backoffice/salesSlice';
import {
  getDashboardTotals,
  getProductSalesTotalsByDate,
  getRevenueOverTimeReport,
} from '../../api/backoffice';
import DateRangeDropdown from '../../components/Reusables/headers/DateRangeDropdown';
import BackOfficeHeaderLocationPicker from '../../components/Reusables/headers/BackOfficeHeaderLocationPicker';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
import {
  getDashboardComparisonPeriodLabel,
  getDashboardDateRange,
  getDashboardRangeDisplayLabel,
  type DashboardRange,
} from '../../utils/dashboardPeriods';
import StatsCardsSection from '../../components/backoffice/StatsCardsSection';
import { type StatCardProps } from '../../components/Reusables/StatCard';
import { getProductRowDisplayName } from '../../utils/productDisplayName';
import { SERVICE_HOUR_UNIT } from '../../utils/productKind';
import {
  showsServiceHoursSold,
  showsServicesSold,
} from '../../utils/serviceSalesFigures';

const getProductSalesDisplayName = (row: any): string =>
  getProductRowDisplayName(row);

const getProductSalesQtyLabel = (row: any): string => {
  const product = row?.productInformation || {};
  const priceType = String(
    row?.priceType || product?.bp_product_price_type || '',
  );
  if (priceType === 'unit') {
    const mass = Number(row?.totalMassSold ?? 0);
    const massFormatted = formatNumberDecimal(mass);
    const uom = String(product?.bp_product_unit_of_mass || '').trim();
    return uom ? `${massFormatted} ${uom}` : massFormatted;
  }
  const qty = Number(row?.totalQuantitySold ?? 0);
  return formatWithCommas(qty, getNumericDisplayDecimals(qty), true);
};

const BackOfficeSalesScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = createStyles(theme);
  const { width: windowWidth } = useWindowDimensions();
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const nav = useNavigation<any>();
  const route = useRoute<any>();
  const rangeParam: DashboardRange | undefined = route.params?.range;
  const [selectedRange, setSelectedRange] = useState<DashboardRange>('Monthly');
  // Fetch with the tapped period on the first render, as Expenses does, so a
  // default 'Monthly' request never races it.
  const activeRange = rangeParam ?? selectedRange;

  // The dashboard's Sales card passes its period so this screen opens on the
  // same totals that were tapped. Adopt it once, then drop the param so the
  // dropdown stays in charge.
  useEffect(() => {
    if (!rangeParam) return;
    setSelectedRange(rangeParam);
    dispatch(setTransactionsPagination({ page: 1 }));
    nav.setParams({ range: undefined });
  }, [rangeParam, dispatch, nav]);

  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const locationVersion = useAppSelector(s => s.location.locationVersion);

  const salesOverTime = useAppSelector(s => s.backofficeSales.salesOverTime);
  const salesTransactions = useAppSelector(s => s.backofficeSales.transactions);
  // Adopting the param also resets the table to page 1 (effect above); use
  // that page on the same render so the old page is not fetched first.
  const transactionsPage = rangeParam ? 1 : salesTransactions.page;
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );

  const totals = useAppSelector(s => s.backofficeSales.totals);
  const totalsLoading = useAppSelector(s => s.backofficeSales.loadingTotals);
  const comparisonPeriodLabel = useMemo(
    () => getDashboardComparisonPeriodLabel(selectedRange),
    [selectedRange],
  );
  const salesStats = useMemo<StatCardProps[]>(
    () => [
      {
        label: 'Sales',
        icon: 'cash-multiple',
        iconColor: theme.colors.successEmerald,
        valuePrefix: currencyText,
        value: `${formatMoney(Number(totals?.totalSales || 0))}`,
        comparison: totals?.comparison?.totalSales,
        comparisonPeriodLabel,
        backgroundColor: theme.colors.successEmeraldSurface,
        borderColor: theme.colors.successEmerald,
      },
      {
        label: 'Profit',
        icon: 'trending-up',
        iconColor: theme.colors.primary,
        valuePrefix: currencyText,
        value: `${formatMoney(Number(totals?.totalProfit || 0))}`,
        comparison: totals?.comparison?.totalProfit,
        comparisonPeriodLabel,
      },
      {
        label: 'Expenses',
        icon: 'cash-plus',
        iconColor: theme.colors.error,
        valuePrefix: currencyText,
        value: `${formatMoney(Number(totals?.totalExpenses || 0))}`,
        comparison: totals?.comparison?.totalExpenses,
        comparisonPeriodLabel,
        backgroundColor: theme.colors.errorSurface,
        borderColor: theme.colors.errorLight,
        onPress: () => nav.navigate('Expenses', { range: selectedRange }),
      },
      {
        label: 'Orders',
        icon: 'receipt-text-outline',
        iconColor: theme.colors.primary,
        value: `${formatWithCommas(Number(totals?.totalOrders || 0), 0)}`,
        comparison: totals?.comparison?.totalOrders,
        comparisonPeriodLabel,
      },
      {
        label: 'Quantity Sold',
        icon: 'package-variant-closed',
        iconColor: theme.colors.warning,
        value: `${formatWithCommas(Number(totals?.totalQuantitiesSold || 0), 0)}`,
        comparison: totals?.comparison?.totalQuantitiesSold,
        comparisonPeriodLabel,
      },
      {
        label: 'Mass Sold',
        icon: 'scale-balance',
        iconColor: theme.colors.info,
        value: `${formatNumberDecimal(Number(totals?.totalMassSold || 0))}`,
        valueSuffix: 'Kg|Lt|Mg',
        comparison: totals?.comparison?.totalMassSold,
        comparisonPeriodLabel,
      },
      // Last, so the pair sits in the Quantity and Mass Sold rows
      ...(showsServicesSold(totals)
        ? [
            {
              label: 'Services Sold',
              icon: 'briefcase-outline',
              iconColor: theme.colors.warning,
              value: `${formatWithCommas(Number(totals?.totalServicesSold || 0), 0)}`,
              comparison: totals?.comparison?.totalServicesSold,
              comparisonPeriodLabel,
            } as StatCardProps,
          ]
        : []),
      ...(showsServiceHoursSold(totals)
        ? [
            {
              label: 'Hours Sold',
              icon: 'clock-outline',
              iconColor: theme.colors.info,
              value: `${formatNumberDecimal(Number(totals?.totalServiceHoursSold || 0))}`,
              valueSuffix: SERVICE_HOUR_UNIT,
              comparison: totals?.comparison?.totalServiceHoursSold,
              comparisonPeriodLabel,
            } as StatCardProps,
          ]
        : []),
    ],
    [comparisonPeriodLabel, currencyText, theme, totals, nav, selectedRange],
  );

  const headerRightNode = useMemo(
    () => (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginRight: s(8),
        }}>
        <View style={{ marginLeft: Platform.OS === 'web' ? s(8) : 0 }}>
          <DateRangeDropdown
            variant="compact"
            chipBgColor={'rgba(255,255,255,0.15)'}
            chipTextColor={theme.colors.textInverse}
            chipBorderColor={'rgba(255,255,255,0.35)'}
            selectedOverride={selectedRange}
            onChange={range => {
              setSelectedRange(range);
              dispatch(setTransactionsPagination({ page: 1 }));
            }}
          />
        </View>
      </View>
    ),
    [selectedRange, dispatch],
  );

  useLayoutEffect(() => {
    nav.setOptions({
      headerRight: () => headerRightNode,
    });
  }, [nav, headerRightNode]);

  const webHeaderNode = useMemo(
    () => (
      <WebHeaderBar
        title={<BackOfficeHeaderLocationPicker />}
        titleAlign="center"
        rightSlot={headerRightNode}
        onBack={() => {
          try {
            nav.navigate('Dashboard');
          } catch {
            if (nav.canGoBack()) nav.goBack();
          }
        }}
      />
    ),
    [headerRightNode, nav],
  );
  useWebHeader(webHeaderNode, true);

  const columns: AppDataTableColumn[] = useMemo(() => {
    if (Platform.OS === 'web') {
      const containerMaxWidth = WEB_MAX_CONTENT_WIDTH;
      const containerPadding = s(24) * 2;
      const tablePadding = s(12);
      const availableWidth =
        Math.min(windowWidth, containerMaxWidth) -
        containerPadding -
        tablePadding;

      // # stays tight; product capped; leftover split evenly across the rest
      const indexWidth = s(44);
      const productWidth = Math.min(
        s(260),
        Math.max(s(180), availableWidth * 0.28),
      );
      const otherCount = 5;
      const otherWidth = Math.max(
        s(100),
        (availableWidth - indexWidth - productWidth) / otherCount,
      );

      return [
        {
          title: '#',
          width: indexWidth,
          wrap: false,
          align: 'center',
        },
        {
          title: 'Product Name',
          width: productWidth,
          wrap: true,
          align: 'left',
        },
        {
          title: 'Category',
          width: otherWidth,
          wrap: true,
          align: 'center',
        },
        {
          title: 'Selling Value',
          width: otherWidth,
          align: 'center',
        },
        {
          title: 'Quantity',
          width: otherWidth,
          align: 'center',
        },
        {
          title: 'Total Sales',
          width: otherWidth,
          align: 'center',
        },
        {
          title: 'Estimated Profit',
          width: otherWidth,
          align: 'center',
        },
      ];
    }
    // Native: keep # + Product + Sales on-screen like Dashboard; rest scroll
    const cardInset = s(10) * 2;
    const availableWidth = Math.max(0, windowWidth - cardInset);
    const indexWidth = s(40);
    const salesWidth = s(120);
    const productWidth = Math.min(
      s(220),
      Math.max(s(160), availableWidth - indexWidth - salesWidth),
    );

    return [
      { title: '#', width: indexWidth, wrap: false, align: 'center' },
      {
        title: 'Product',
        width: productWidth,
        wrap: true,
        align: 'left',
      },
      {
        title: 'Sales',
        width: salesWidth,
        align: 'center',
      },
      { title: 'Quantity', width: s(110), align: 'center' },
      {
        title: 'Estimated Profit',
        width: s(150),
        align: 'right',
      },
      { title: 'Category', width: s(150) },
      {
        title: 'Buying Value',
        width: s(150),
        align: 'right',
      },
      {
        title: 'Selling Value',
        width: s(150),
        align: 'right',
      },
    ];
  }, [windowWidth]);

  const tableData = salesTransactions.items.map((row: any, idx) => {
    const product = row?.productInformation || {};
    const name = getProductSalesDisplayName(row);
    const category = String(product?.bp_product_category || '');
    const buyingValue = Number(row?.buyingValue ?? 0);
    const sellingValue = Number(row?.sellingValue ?? 0);
    const qty = getProductSalesQtyLabel(row);
    const totalSales = Number(row?.totalSalesAmount ?? 0);
    const profit = Number(row?.estimatedProfit || 0);
    const rowIndex = String(
      (salesTransactions.page - 1) * salesTransactions.pageSize + idx + 1,
    );

    if (Platform.OS === 'web') {
      return [
        rowIndex,
        name,
        category,
        `${formatMoney(sellingValue)}`,
        qty,
        `${formatMoney(totalSales)}`,
        `${formatMoney(profit)}`,
      ];
    }
    return [
      rowIndex,
      name,
      `${formatMoney(totalSales)}`,
      qty,
      `${formatMoney(profit)}`,
      category,
      `${formatMoney(buyingValue)}`,
      `${formatMoney(sellingValue)}`,
    ];
  });

  const fetchSalesOverTime = useCallback(async () => {
    if (!owner?.accessToken || !ownerId) return;
    const { start, end, periodKey } = getDashboardDateRange(activeRange);
    try {
      const res = await getRevenueOverTimeReport(
        axiosBackoffice as any,
        ownerId,
        start,
        end,
        { accessToken: owner?.accessToken },
        periodKey,
      );
      if (res?.status === 200) {
        dispatch(setSalesOverTime(res.data.data));
      } else {
        dispatch(setSalesOverTime(null));
      }
    } catch {
      dispatch(setSalesOverTime(null));
    }
  }, [owner?.accessToken, ownerId, axiosBackoffice, activeRange, dispatch]);

  const fetchTotals = useCallback(async () => {
    if (!owner?.accessToken || !ownerId) return;
    const { start, end, periodKey } = getDashboardDateRange(activeRange);
    try {
      dispatch(setSalesTotalsLoading(true));
      dispatch(setSalesTotalsError(null));
      const res = await getDashboardTotals(
        axiosBackoffice as any,
        ownerId,
        start,
        end,
        { accessToken: owner?.accessToken },
        periodKey,
      );
      if (res?.status === 200) {
        dispatch(setSalesTotals(res.data.data as any));
      } else {
        dispatch(
          setSalesTotalsError(
            String(res?.data?.message || 'Failed to load totals'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(setSalesTotalsError(e?.message || 'Error fetching totals'));
    } finally {
      dispatch(setSalesTotalsLoading(false));
    }
  }, [owner?.accessToken, ownerId, axiosBackoffice, activeRange, dispatch]);

  const fetchTransactions = useCallback(async () => {
    if (!owner?.accessToken || !ownerId) return;
    const { start, end } = getDashboardDateRange(activeRange);
    try {
      dispatch(setTransactionsLoading(true));
      dispatch(setTransactionsError(null));
      const res = await getProductSalesTotalsByDate(
        axiosBackoffice as any,
        ownerId,
        transactionsPage,
        salesTransactions.pageSize,
        start,
        end,
        { accessToken: owner?.accessToken },
      );
      if (res?.status === 200) {
        const payload = res?.data?.data || {};
        const docs = payload?.data || payload?.docs || [];
        const total = payload?.total || payload?.totalDocs || docs.length;
        const totalPages = payload?.totalPages || 0;
        const totalAmount = payload?.grandTotalSalesAmount || 0;
        const currentPage = payload?.currentPage;
        const perPage = payload?.perPage;
        const items = docs as any[];
        dispatch(
          setTransactions({
            items,
            total,
            page: transactionsPage,
            pageSize: salesTransactions.pageSize,
            totalPages,
            totalAmount,
            currentPage,
            perPage,
            grandTotals: {
              grandTotalSalesAmount: Number(payload?.grandTotalSalesAmount ?? 0),
              grandTotalBuyingValue: Number(
                payload?.grandTotalBuyingValue ?? 0,
              ),
              grandTotalEstimatedProfit: Number(
                payload?.grandTotalEstimatedProfit ?? 0,
              ),
              grandTotalItemsSold: Number(payload?.grandTotalItemsSold ?? 0),
              grandTotalMassSold: Number(payload?.grandTotalMassSold ?? 0),
            },
          }),
        );
      } else {
        dispatch(
          setTransactionsError(
            String(res?.data?.message || 'Failed to load transactions'),
          ),
        );
      }
    } catch (e: any) {
      dispatch(
        setTransactionsError(e?.message || 'Error fetching transactions'),
      );
    } finally {
      dispatch(setTransactionsLoading(false));
    }
  }, [
    owner?.accessToken,
    ownerId,
    axiosBackoffice,
    dispatch,
    activeRange,
    transactionsPage,
    salesTransactions.pageSize,
  ]);

  useEffect(() => {
    fetchSalesOverTime();
  }, [fetchSalesOverTime, locationVersion]);

  useEffect(() => {
    fetchTotals();
  }, [fetchTotals, locationVersion]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions, locationVersion]);

  return (
    <View style={styles.container}>

      <ScrollView contentContainerStyle={{ rowGap: v(12), flexGrow: 1 }}>
        <StatsCardsSection
          stats={salesStats}
          loading={totalsLoading}
        />
        <View style={styles.card}>
          <Text style={styles.cardTitle}>
            Sales ({getDashboardRangeDisplayLabel(activeRange)})
          </Text>
          <PaymentsBarChart
            data={(salesOverTime?.series ?? []) as any}
            valueLabel="Sales"
          />
        </View>

        <View style={styles.tableContainer}>
          <Text
            style={[
              styles.headText,
              {
                fontSize: fs(17),
                lineHeight: fs(20),
                marginTop: v(10),
                marginBottom: v(16),
              },
            ]}>
            All Product Sales
          </Text>
          <AppDataTable
            columns={columns}
            data={tableData}
            loading={salesTransactions.loading}
            showRotateToggle={false}
            keyExtractor={(_, index) => String(index)}
            footerPagination={{
              page: Math.max(0, salesTransactions.page - 1),
              numberOfPages: Math.max(
                1,
                Math.ceil(salesTransactions.total / salesTransactions.pageSize),
              ),
              onPageChange: p =>
                dispatch(setTransactionsPagination({ page: p + 1 })),
              label: `${(salesTransactions.page - 1) * salesTransactions.pageSize + 1}-${Math.min(
                salesTransactions.page * salesTransactions.pageSize,
                salesTransactions.total,
              )} of ${salesTransactions.total}`,
              showFastPaginationControls: true,
              numberOfItemsPerPageList: [100, 200, 500],
              numberOfItemsPerPage: salesTransactions.pageSize,
              onItemsPerPageChange: size =>
                dispatch(
                  setTransactionsPagination({
                    page: 1,
                    pageSize: Number(size || 100),
                  }),
                ),
              selectPageDropdownLabel: 'Rows per page',
            }}
          />
        </View>
      </ScrollView>
    </View>
  );
};

export default BackOfficeSalesScreen;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        padding: s(24),
      }),
    },
    title: {
      fontSize: fs(20),
      lineHeight: fs(24),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      marginBottom: v(6),
    },
    subtitle: {
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      margin: v(10),
    },
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      marginHorizontal: s(10),
      padding: s(2),
      ...theme.shadows.sm,
    },
    cardTitle: {
      fontFamily: GTFonts.bold,
      textAlign: 'center',
      color: theme.colors.text,
      fontSize: fs(18),
      lineHeight: fs(20),
      marginVertical: v(8),
    },
    tableContainer: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      marginHorizontal: s(10),
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      ...theme.shadows.md,
    },
    tableBorder: {
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    head: {
      height: v(56),
      backgroundColor: theme.colors.tableHeader,
    },
    headText: {
      margin: s(8),
      fontSize: fs(14),
      fontFamily: GTFonts.bold,
      color: theme.colors.tableHeaderText,
      textAlign: 'center',
    },
    dataWrapper: {
      maxHeight: v(400),
    },
    row: {
      height: v(60),
      backgroundColor: theme.colors.surface,
    },
    rowEven: {
      backgroundColor: theme.colors.surface,
    },
    rowOdd: {
      backgroundColor: theme.colors.surfaceSecondary,
    },
    rowText: {
      margin: s(8),
      fontSize: fs(14),
      fontFamily: GTFonts.regular,
      color: theme.colors.text,
      textAlign: 'center',
    },
    paginationContainer: {
      backgroundColor: theme.colors.surface,
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
      paddingVertical: s(16),
      paddingHorizontal: s(16),
    },
    paginationInfo: {
      alignItems: 'center',
      marginBottom: v(12),
    },
    paginationText: {
      fontSize: fs(14),
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
    },
    paginationBtns: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    paginationBtn: {
      backgroundColor: theme.colors.primary,
      paddingHorizontal: s(20),
      paddingVertical: s(12),
      borderRadius: theme.borderRadius.md,
      minWidth: s(80),
      alignItems: 'center',
    },
    paginationBtnDisabled: {
      backgroundColor: theme.colors.border,
    },
    paginationBtnText: {
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      color: theme.colors.textInverse,
    },
    paginationBtnTextDisabled: {
      color: theme.colors.textSecondary,
    },
    pageIndicator: {
      backgroundColor: theme.colors.surfaceSecondary,
      paddingHorizontal: s(16),
      paddingVertical: s(8),
      borderRadius: theme.borderRadius.md,
    },
    pageText: {
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
    loadingWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(10),
    },
  });
